/**
 * Чтение рекламного конфига, приехавшего из БД в `window.initial.context.adConfig`.
 *
 * Форма: `{slot: {variant: [payload, ...]}}`. Плечи ротации уже выбраны на сервере,
 * поэтому здесь ничего не разыгрывается — что пришло, то и рендерим.
 *
 * Если конфига нет (кампания ещё не перенесена или выключена флагом `is_active`),
 * резолв возвращает null, и вызывающий код остаётся на конфиге из бандла —
 * с той семантикой фолбэка, которая у его шаблона сейчас.
 */

import { pickVariant } from "./adVariant";

// Типы яндексовых блоков, привязанных к вьюпорту: контейнер им не нужен.
const NO_CONTAINER_TYPES = new Set(["fullscreen", "overlay", "floorAd"]);

export function getAdConfig() {
    if (typeof window === "undefined" || !window.initial || !window.initial.context) {
        return null;
    }

    return window.initial.context.adConfig || null;
}

// Режим БД решается по кампании целиком, а не по слоту: пустой объект — тоже режим БД.
export function isDbMode(adConfig = getAdConfig()) {
    return adConfig !== null && adConfig !== undefined;
}

// Учёт источника: откуда фактически взялся каждый слот — из БД, из бандла или ниоткуда
// (кампания в БД, а слот в ней не заведён — и потому не показан).
// Слоты спрашивают поштучно во время рендера, поэтому решения копятся и уходят
// в консоль одной строкой на следующем тике: иначе на страницу пришлось бы
// два десятка сообщений.
const slotSource = {};
let reportScheduled = false;
// Сколько слотов вошло в последний отчёт: отличать «ничего не изменилось» от
// «появились новые слоты» нужно, чтобы поздний рендер не остался без строки.
let reportedSlots = -1;

const SOURCE_DB = "db";
const SOURCE_BUNDLE = "bundle";
const SOURCE_MISSING = "missing";

function noteSource(slot, fromDb, adConfig) {
    if (slot in slotSource) {
        return;
    }

    slotSource[slot] = fromDb ? SOURCE_DB : isDbMode(adConfig) ? SOURCE_MISSING : SOURCE_BUNDLE;
    scheduleSourceReport();
}

function scheduleSourceReport() {
    if (reportScheduled || typeof window === "undefined" || typeof console === "undefined") {
        return;
    }

    reportScheduled = true;

    setTimeout(() => {
        reportScheduled = false;

        const slots = Object.keys(slotSource).sort();

        if (slots.length === reportedSlots) {
            return;
        }

        reportedSlots = slots.length;

        const bySource = (source) => slots.filter((slot) => slotSource[slot] === source).join(", ") || "—";

        console.info(
            "[ad-config] кампания: %s | из БД: %s | из бандла: %s | нет в БД (не показан): %s",
            isDbMode() ? "есть в БД" : "в БД не заведена",
            bySource(SOURCE_DB),
            bySource(SOURCE_BUNDLE),
            bySource(SOURCE_MISSING),
        );
    }, 0);
}

// Отчёт печатается и тогда, когда конфиг не спросил ни один слот. Без этого лог
// молчал на превью (`?fpr=...` без `view=full`): ir-блоки там не рендерятся, спрашивать
// конфиг некому — и молчание читалось как «лог сломан», хотя верный ответ другой:
// «эту страницу конфиг из БД не касается».
//
// Задержка, а не `load` или ближайший тик: порядок «модуль — рендер — load» зависит от
// того, как подключён бандл, и на нулевой задержке пустой отчёт обгонял слоты — строк
// становилось две. Слот, спросивший конфиг, печатает строку сам, а этот отчёт к своему
// сроку увидит, что список не изменился, и промолчит.
const INITIAL_REPORT_DELAY = 1500;

if (typeof window !== "undefined") {
    setTimeout(scheduleSourceReport, INITIAL_REPORT_DELAY);
}

/** Есть ли слот в конфиге из БД (для любого варианта). */
export function hasSlot(slot, adConfig = getAdConfig()) {
    const fromDb = Boolean(adConfig && adConfig[slot]);

    noteSource(slot, fromDb, adConfig);

    return fromDb;
}

export const OUTCOME_BUNDLE = "bundle";
export const OUTCOME_ENTRIES = "entries";
export const OUTCOME_JSON = "json";
export const OUTCOME_NONE = "none";

// Исходы резолва слота на текущем варианте (full/prev × pc/mob):
// bundle — кампании нет в БД, рисует бандл; entries — блоки из БД; json — место
// заполняет тизерная лента; none — кампания в БД, а показывать нечего (слот или
// вариант не заведён, либо плечо ротации проиграло), в бандл уходить нельзя.
export function resolveSlotOutcome(slot, { isFull, isDesktop, adConfig = getAdConfig() } = {}) {
    if (!isDbMode(adConfig)) {
        noteSource(slot, false, adConfig);

        return { kind: OUTCOME_BUNDLE };
    }

    const all = adConfig[slot] ? adConfig[slot][pickVariant(isFull, isDesktop)] : null;

    if (!all) {
        noteSource(slot, false, adConfig);

        return { kind: OUTCOME_NONE };
    }

    // Пустой список — плечо ротации проиграло: слот в кампании заведён, показывать
    // нечего, и второе плечо той же группы из бандла тут рисовать нельзя.
    if (!all.length) {
        noteSource(slot, true, adConfig);

        return { kind: OUTCOME_NONE };
    }

    // provider=json — это не блок, а id ленты, из которой место заполняется
    // тизерами. Рендерят её ToPad/Feed/InRead по своим срезам, не AdSlot.
    // adfox отключён: его записи место не занимают, даже если ещё лежат в БД.
    const entries = all.filter((entry) => entry.provider !== "json" && entry.provider !== "adfox");

    noteSource(slot, true, adConfig);

    return entries.length ? { kind: OUTCOME_ENTRIES, entries } : { kind: OUTCOME_JSON };
}

// null — рисует вызывающий код (бандл или тизерный компонент), [] — показывать
// нечего, непустой массив — блоки из БД.
export function resolveSlot(slot, options) {
    const outcome = resolveSlotOutcome(slot, options);

    if (outcome.kind === OUTCOME_ENTRIES) {
        return outcome.entries;
    }

    return outcome.kind === OUTCOME_NONE ? [] : null;
}

/**
 * id ленты `fetchJsonAds` для текущего варианта.
 *
 * Лежит на местах, которые лента заполняет (provider='json'), поэтому берём первый
 * попавшийся — значение у всех одно.
 */
export function resolveJsonObjectId({ isFull, isDesktop, fallback = null, adConfig = getAdConfig() } = {}) {
    if (!isDbMode(adConfig)) {
        noteSource("json(objectId)", false, adConfig);

        return fallback;
    }

    const variant = pickVariant(isFull, isDesktop);

    for (const byVariant of Object.values(adConfig)) {
        for (const entry of byVariant[variant] || []) {
            if (entry.provider === "json" && entry.blockId) {
                noteSource("json(objectId)", true, adConfig);

                return entry.blockId;
            }
        }
    }

    // Кампания в БД, а ленты на этом варианте нет — тизерам взяться неоткуда.
    noteSource("json(objectId)", false, adConfig);

    return null;
}

/**
 * Слот проиграл ротацию: в кампании он заведён, но показывать на нём нечего.
 *
 * Отличать обязательно: если уйти отсюда в конфиг из бандла, на странице окажется
 * второе плечо той же группы — то самое, которое сервер и не выбрал.
 */
function isSilenced(entries) {
    return Array.isArray(entries) && entries.length === 0;
}

/** Первый плейсмент слота или null — и для незаведённого слота, и для проигравшего плеча. */
export function resolveSlotEntry(slot, options) {
    const entries = resolveSlot(slot, options);

    return entries && entries.length ? entries[0] : null;
}

/**
 * Значение слота, который передаётся пропом, а не рендерится компонентом:
 * `refresh` (слаг страницы), `backclick`/`popunder` (smi placement-id), `sidebar`
 * (яндексовый блок внутри колонки).
 *
 * Второй способ чтения конфига после `AdSlot`, и больше их нет. Разделение
 * проходит по тому, чем слот является в шаблоне: если слот — это весь блок,
 * его рисует `AdSlot`; если слот — значение внутри чужого блока, оно читается
 * отсюда. Третьего не нужно: `field` покрывает слоты, у которых значение лежит
 * не в `blockId` (у `refresh` это `pageUrl` — слаг, а не id информера).
 *
 * Возвращает `fallback`, только если кампании нет в БД, — вызывающий код
 * остаётся на значении из бандла. В режиме БД промах — это null.
 */
export function resolveSlotValue(
    slot,
    { isFull, isDesktop, fallback = null, field = "blockId", adConfig = getAdConfig() } = {},
) {
    const entries = resolveSlot(slot, { isFull, isDesktop, adConfig });

    if (isSilenced(entries)) {
        noteSource(slot, true, adConfig);

        return null;
    }

    const entry = entries && entries.length ? entries[0] : null;
    const value = entry ? entry[field] : null;
    // Запись без нужного поля равносильна её отсутствию: у `refresh` бывает только
    // `blockId` информера, а слага нет — тогда редиректа нет (в режиме БД) или он
    // бандловый (кампании в БД нет).
    const fromDb = value !== null && value !== undefined;

    noteSource(slot, fromDb, adConfig);

    if (fromDb) {
        return value;
    }

    return isDbMode(adConfig) ? null : fallback;
}

// Слот refresh — несколько значений сразу: слаг своей страницы, флаг редиректа по RSS,
// свой адрес RSS (обычно пуст — тогда REFRESH_RSS_URL) и время до редиректа (delay, мс).
// Слаг при RSS остаётся запасным путём, поэтому читаются все. В бандле нет ни RSS,
// ни своего времени — там время потока.
export function resolveRefresh({ isFull, isDesktop, fallback = null, adConfig = getAdConfig() } = {}) {
    const page = resolveSlotValue("refresh", { isFull, isDesktop, fallback, field: "pageUrl", adConfig });
    const entry = isDbMode(adConfig) ? resolveSlotEntry("refresh", { isFull, isDesktop, adConfig }) : null;

    return {
        page,
        useRss: Boolean(entry && entry.useRss),
        rssUrl: (entry && entry.rssUrl) || null,
        delay: (entry && entry.delay) || null,
    };
}

// Настройки поведения слота приезжают в каждом его плейсменте: отдельной ветки
// в конфиге под них нет, чтобы не менять форму `{слот: {вариант: [блоки]}}`.
const SETTING_KEYS = ["maxWidgets", "yandexOnly", "isSticky"];

/**
 * Все значения слота списком — для пропов, принимающих несколько блоков сразу
 * (колонка виджетов в сайдбаре 2013: в бандле там массив из четырёх id).
 *
 * Отдельная функция, а не флаг у `resolveSlotValue`: слот с четырьмя записями и
 * слот с одной — разные вещи для вызывающего кода, и молчаливое приведение одного
 * к другому уже стоило трёх виджетов из четырёх.
 */
export function resolveSlotValues(
    slot,
    { isFull, isDesktop, fallback = null, field = "blockId", adConfig = getAdConfig() } = {},
) {
    const entries = resolveSlot(slot, { isFull, isDesktop, adConfig });

    if (isSilenced(entries)) {
        noteSource(slot, true, adConfig);

        return [];
    }

    const values = (entries || [])
        .map((entry) => entry[field])
        .filter((value) => value !== null && value !== undefined);

    noteSource(slot, values.length > 0, adConfig);

    if (values.length) {
        return values;
    }

    return isDbMode(adConfig) ? [] : fallback;
}

/**
 * Настройки поведения слота из БД либо null, если кампании нет или настройки не заданы.
 *
 * Возвращает только то, что действительно настроено. Чем подставиться вместо
 * незаполненного, решает вызывающий код: вне режима БД — значением из бандла,
 * в режиме БД — нейтральным значением по умолчанию, а не бандлом.
 */
export function resolveSlotSettings(slot, { isFull, isDesktop, adConfig = getAdConfig() } = {}) {
    // Запись берётся с json-местами включительно: настройки приезжают в каждом
    // плейсменте слота, а у json-сайдбара других плейсментов и нет.
    const byVariant = isDbMode(adConfig) && adConfig[slot] ? adConfig[slot][pickVariant(isFull, isDesktop)] : null;
    const entry = byVariant && byVariant.length ? byVariant[0] : null;

    if (!entry) {
        return null;
    }

    const settings = {};

    for (const key of SETTING_KEYS) {
        if (entry[key] !== undefined) {
            settings[key] = entry[key];
        }
    }

    return Object.keys(settings).length ? settings : null;
}

/** Нужен ли яндексовому блоку свой контейнер. */
export function needsContainer(entry) {
    return !NO_CONTAINER_TYPES.has(entry.type);
}

/** id контейнера: явный renderTo, иначе конвенция `yandex_rtb_<blockId>`. */
export function containerId(entry) {
    return entry.renderTo || `yandex_rtb_${entry.blockId}`;
}
